<template>
  <div class="screen-root safe-area" ref="root">
    <div v-if="showIntro" class="intro-text">
      {{ introText || screenName }}
    </div>
    <template v-else>
      <div
        class="screen-titles"
        :class="{ invisible: blank && !error, 'justify-center': centerTitles }"
      >
        <img v-if="error" class="error-icon" src="../../assets/common/error.png" alt="Error" />
        <h2
          class="screen-name"
          :class="{ 'text-center': centerTitles, 'text-purple-gradient': purpleTitle }"
        >
          {{ screenName }}
        </h2>
        <div v-if="step" class="step-wrapper">
          <h1 class="step">{{ step }}</h1>
          <h1 class="screen-info" :class="{ error }" v-html="screenInfo"></h1>
        </div>
        <h1 v-else class="screen-info" :class="{ error }" v-html="screenInfo"></h1>
      </div>
      <!--
        Three parts: the titles above, this scrolling middle, and an optional
        footer below. The footer sits outside the wrapper, so a screen that puts
        its primary button there keeps it in view while the content moves.
      -->
      <div
        class="screen-scroll"
        ref="scroll"
        :class="{ scrollable: isScrollable || scrollable, 'optical-center': !$slots.footer }"
      >
        <div class="content-container" ref="content" :class="{ 'no-padding': noPadding }">
          <slot></slot>
        </div>
        <!--
          No caller of this component passes altInfo — every :alt-info in the
          tree goes to Stamp/Screen.vue — so this was an empty <p> charging
          every screen the 128px of mb-32 below it. That is what squashed the
          fixed h-40 title band, and what the scrolling middle would otherwise
          have to scroll through to reach nothing.
        -->
        <p v-if="altInfo" class="alt-info" v-html="altInfo"></p>
      </div>
      <div v-if="$slots.footer" class="screen-footer">
        <slot name="footer"></slot>
      </div>
    </template>
    <div class="left-btn absolute" v-if="canSkip && !showIntro">
      <span @click="$emit('skip')">{{ skipText || $t('scanscreen.skip') }}</span>
    </div>
    <div class="right-btn absolute p-3" v-if="canLeave && !showIntro">
      <img src="../../assets/common/close-icon.svg" @click="$emit('leave')" alt="Leave" />
    </div>
    <slot name="audio"></slot>
  </div>
</template>

<script>
import scanScreen from '@/mixins/scan-screen';

export default {
  name: 'Screen',
  mixins: [scanScreen],
  data: () => ({
    isScrollable: false,
  }),
  mounted() {
    this.checkIfLastElementIsOutOfRoot();
    window.addEventListener('resize', this.checkIfLastElementIsOutOfRoot);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkIfLastElementIsOutOfRoot);
  },
  methods: {
    checkIfLastElementIsOutOfRoot() {
      if (!this.$refs.content) return;

      const lastElement = this.$refs.content.lastElementChild;
      if (!lastElement) return;

      // Measured against the scrolling wrapper, not the root: with a footer the
      // root now extends below the wrapper, and comparing against the root
      // would call content that already runs under the footer a fit.
      const { content, scroll } = this.$refs;
      const padBottom = parseFloat(getComputedStyle(content).paddingBottom) || 0;
      const contentEnd = lastElement.getBoundingClientRect().bottom + padBottom;
      this.isScrollable = contentEnd > scroll.getBoundingClientRect().bottom + 1;
    },
  },
};
</script>

<style lang="scss" scoped>
.screen-root {
  @apply flex-col w-full h-full flex items-center;
  // How much height the fixed title band below claims, its top margin
  // included. The counterweight in .screen-scroll reads the same value, so
  // moving a breakpoint moves both ends together instead of drifting apart.
  --title-band: 14rem; // mt-16 + h-40
  @screen sm {
    --title-band: 15rem; // mt-20 + h-40
  }
  @screen md {
    --title-band: 21rem; // mt-20 + h-64
  }
  @screen lg {
    --title-band: 22rem; // mt-24 + h-64
  }
}

.screen-scroll {
  @apply flex flex-col items-center w-full h-full;
  // Without this a flex item refuses to shrink below its content's min-content
  // height, so on a short screen this wrapper hung 59px past the root instead
  // of letting the counterweight below give its room back — and #app clips
  // whatever ends up past the root.
  min-height: 0;
  &.scrollable {
    @apply overflow-y-auto;
  }
  // The title band is pinned above this wrapper, so the wrapper only ever gets
  // the space under it and its centre sits half a title band below the screen's
  // own. Anything centred with auto margins — the Doll, FeetMark — therefore
  // lands in the bottom half. Pad the far end by the same amount to bring that
  // centre back up.
  //
  // It is a shrinkable flex item, not padding: once the content needs the room
  // the counterweight gives it all back rather than clipping the content or
  // leaving a tail of blank space to scroll through. Screens with a footer are
  // left alone — their footer already weighs the bottom down.
  //
  // The lopsided shrink factor is the point. Flex shares an overflow out among
  // every item that can give, so a plain `flex: 0 1` counterweight kept 91px of
  // itself on /discovery at 375x667 and squeezed the cards to pay for it. At
  // 999 it is the one that yields, all the way to zero, before any real content
  // is asked for a single pixel.
  //
  // It is a sibling of .content-container rather than something inside it so
  // that it shortens the box itself. Screens like ScanBody make
  // .content-container relative and stack absolutely positioned overlays on it
  // — the countdown, the scan-complete mark — and those centre on its padding
  // box. A counterweight living inside would move the flow content out from
  // under them and leave the overlays behind: the countdown sat 77px off the
  // doll it is supposed to ring, where the design puts it 34.5px above.
  &.optical-center::after {
    content: '';
    flex: 0 999 var(--title-band);
  }
}

// Screens passing no footer render nothing here, so they are untouched.
.screen-footer {
  @apply flex flex-col items-center w-full flex-shrink-0 pt-6 pb-3;
}
.screen-titles {
  @apply flex relative flex-col w-full text-left h-40 mt-16 px-8;
  @screen sm {
    @apply mt-20 px-20;
  }
  @screen md {
    @apply h-64;
  }
  @screen lg {
    @apply mt-24 px-24;
  }
  .error-icon {
    @apply absolute mx-auto;
    width: 5%;
    left: 0;
    right: 0;
    top: -15%;
  }
}
.screen-name {
  @apply text-2xl;
  font-weight: 500;
  line-height: 1.25;
  color: #5b5b5b;
  @screen xs {
    @apply text-lg;
  }
  @screen lg {
    @apply text-4xl;
  }
}
.step-wrapper {
  @apply flex flex-row mt-2;
  .screen-info {
    @apply text-left text-base font-semibold;
    line-height: 20px;
  }
}
.step {
  @apply text-xl text-center mt-5 mx-5;
  font-family: Roboto;
  font-weight: 400;
  font-size: 70px;
  line-height: 60px;
}
.screen-info {
  @apply text-xl text-center mt-5;
  font-weight: bold;
  line-height: 1.5;
  color: #2c2c2c;
  min-height: 6rem;
  @screen sm {
    @apply text-4xl;
  }
  @screen lg {
    @apply text-5xl;
  }
  &.error {
    @apply font-bold;
    color: #b32223;
  }
}
.intro-text {
  @apply mx-auto text-center flex flex-1 flex-col justify-center overflow-visible w-full pt-10;
  font-size: 33px;
}
.content-container {
  @apply flex flex-1 flex-col items-center overflow-visible w-full px-6 py-4;
  min-height: 0;
  @screen sm {
    @apply px-20;
  }
  &.no-padding {
    padding: 0 !important;
  }
}
.skip-btn {
  @apply absolute top-0 left-0 mt-10 ml-12 cursor-pointer;
  color: rgba(38, 38, 38, 0.4);

  @screen xs {
    @apply mt-4 ml-5;
    margin-top: calc(constant(safe-area-inset-top, -1rem) + 1rem);
    margin-top: calc(env(safe-area-inset-top, -1rem) + 1rem);
  }
}
.alt-info {
  @apply mb-32 text-center;
  font-size: 18px;
  max-width: 70%;
  @screen lg {
    @apply mb-56;
    font-size: 24px;
  }
}

.right-btn {
  // 5vh is 43px on a 402x874 screen, which put 18px of the close button under
  // the Dynamic Island. Keep 5vh as the floor so a device reporting no inset
  // renders exactly as before, and only push down when there is an inset to
  // clear. .left-btn extends this, so the skip corner is covered too.
  // --safe-top comes from assets/css/common.css; interpolated to keep sass out
  // of max().
  top: #{'max(5vh, calc(var(--safe-top) + 1rem))'};
  right: 1.75rem;
}

.left-btn {
  color: rgba(38, 38, 38, 0.4);
  @extend .right-btn;
  left: 1.75rem;
  transform: translateY(25%);
}
</style>
